import getIntl from '../../../../helpers/intl'
import { logAppError } from 'Helpers/logger'
import { FormattedMessage } from 'react-intl'

import { pageState } from '../state/page'
import {
  contentsState,
  lastUpdatedState,
  listState,
  sourceState,
  ERROR_CODE_EMPTY_FILE,
  ERROR_CODE_INVALID_EXT,
  ERROR_CODE_INVALID_ZIP,
  ERROR_CODE_MAPPING,
  ERROR_CODE_PARSING,
  ERROR_CODE_TOO_MANY_CONTENT,
  ERROR_CODE_VALIDATING_HEADER,
  updateFrequencyStringSelector,
  MAX_CALLS
} from '../state/source'
import { modalSharedListsState, modalShowHistoryState } from '../state/modals'
import {
  getSourceActivity,
  getSourceById,
  getContents,
  getListOfStatus,
  getLastSourceActivity,
  uploadFile,
  getLastUpdateContents,
  getCsv,
  getUserLists,
  getSelectAll,
  getBlockedLists,
  saveSharedLists,
  insertSource,
  updateSource,
  updateContents,
  download,
  getUpdatedSourceActivity
} from '../actions/actions'
import {
  SOURCE_ID_TYPE_FILE,
  STORE_KEY_ACCOUNT,
  SOURCE_ID_TYPE_URL,
  MIME_TYPE_CSV,
  MIME_TYPE_JSON
} from 'Constants/index'

import {
  UPLOADER_VARIANT_DEFAULT,
  toastShow,
  toastDismiss,
  TOAST_VARIANT_WARNING,
  TOAST_VARIANT_ERROR,
  TOAST_VARIANT_INFO,
  TOAST_VARIANT_SUCCESS
} from 'mailup-web-components'
import { storeGet } from 'mailup-react-helpers'
import { STORE_KEY_ID_LIST } from '../../../../constants/index'

export const initCallback =
  ({ set, snapshot }) =>
  async ({ idSource, idType, name = '', url = '' }) => {
    set(pageState, (prev) => ({ ...prev, loading: true }))
    try {
      if (idSource > 0) {
        const sourceData = await getSourceById(idSource)
        set(sourceState, (prev) => ({
          ...prev,
          idSource,
          ...sourceData
        }))

        if (idType === SOURCE_ID_TYPE_URL) {
          set(sourceState, (prev) => ({
            ...prev,
            idSource,
            ...sourceData,
            updateFrequency: {
              ...prev.updateFrequency,
              ...sourceData.updateFrequency
            }
          }))
        }

        if (sourceData.isShared) {
          const [{ lists }, { selectAll }, { blockedLists }] =
            await Promise.all([
              getUserLists(),
              getSelectAll(idSource),
              getBlockedLists(idSource)
            ])
          const concatenetedLists = Array.from(
            new Set(sourceData.sharedLists.concat(blockedLists))
          )
          set(sourceState, (prev) => ({
            ...prev,
            selectAll,
            sharedLists: concatenetedLists,
            blockedLists
          }))
          set(listState, (prev) => ({
            ...prev,
            lists
          }))
        }
        set(contentsState, (prev) => ({
          ...prev,
          loading: true
        }))
        set(lastUpdatedState, (prev) => ({
          ...prev,
          loading: true,
          error: false,
          isEnabled: false
        }))
        set(modalShowHistoryState, (prev) => ({
          ...prev,
          loading: true
        }))
        await Promise.all([
          getLastUpdateContentsCallback({ set, snapshot }, idSource, idType),
          getContentsUpdateContentsCallback(
            { set, snapshot },
            idSource,
            idType
          ),
          getNumberOfContentsCallback({ set, snapshot }, 0, idSource, idType)
        ])

        if (idType === SOURCE_ID_TYPE_FILE) {
          getLastSourceActivityCallback({ set, snapshot }, idSource, idType)
        }
      } else {
        if (idType === SOURCE_ID_TYPE_URL && (name || url)) {
          set(sourceState, (prev) => ({
            ...prev,
            idSource,
            idType,
            name,
            url,
            updateType: 2
          }))
        } else {
          set(sourceState, (prev) => ({
            ...prev,
            idSource,
            idType,
            updateType: idType === SOURCE_ID_TYPE_URL ? 2 : 0
          }))
        }
      }
    } catch (error) {
      logAppError({
        Component: 'SourceEdit',
        Method: 'initCallback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }
    set(pageState, (prev) => ({ ...prev, loaded: true, loading: false }))
  }

const getContentsUpdateContentsCallback = async (
  { set, snapshot },
  idS = 0,
  idT = 0
) => {
  const { idSource, idType } = await snapshot.getPromise(sourceState)
  const sourceId = idS || idSource
  const sourceType = idT || idType

  if (sourceId > 0) {
    try {
      const { items: updateHistory } = await getListOfStatus(
        sourceId,
        sourceType
      )

      let otherActivity
      if (sourceType === SOURCE_ID_TYPE_FILE) {
        otherActivity = (
          await getSourceActivity(sourceId, {
            PageSize: 5,
            PageNumber: 1
          })
        ).items
      }

      set(modalShowHistoryState, (prev) => ({
        ...prev,
        updateHistory,
        otherActivity
      }))
    } catch (error) {
      logAppError({
        Component: 'SourceEdit',
        Method: 'getContentsUpdateContentsCallback',
        appError: error
      })
    }
  }
}

const getLastSourceActivityCallback = async (
  { set, snapshot },
  idS = 0,
  idT = 0,
  activity
) => {
  const { idSource, idType } = await snapshot.getPromise(sourceState)
  const sourceId = idS || idSource
  const sourceType = idT || idType

  try {
    let lastSourceActivity = activity
    if (!activity) {
      const { item } = await getLastSourceActivity(sourceId)
      set(sourceState, (prev) => ({
        ...prev,
        lastActivity: item
      }))
      lastSourceActivity = item
    }

    if (lastSourceActivity) {
      const { Status, ActivityData } = lastSourceActivity
      if (Status === 0 || Status === 1) {
        let toastMessage
        if (sourceType === SOURCE_ID_TYPE_FILE) {
          if (ActivityData) {
            const { UserFilename } = JSON.parse(ActivityData)
            toastMessage = (
              <FormattedMessage
                id='Load.ContentsSouces.LoadAfter10secFile'
                values={{
                  FILE: UserFilename,
                  strong: (chunks) => <strong>{chunks}</strong>
                }}
              />
            )
          }
        } else {
          toastMessage = getIntl().formatMessage({
            id: 'Load.ContentsSouces.LoadAfter10secUrl'
          })
        }
        toastShow(toastMessage, TOAST_VARIANT_WARNING, 'source--load--warning')
      } else if (Status === 2 || Status === -1) {
        set(contentsState, (prev) => ({ ...prev, callNum: 0 }))
        if (Status === -1) {
          if (ActivityData) {
            const { ErrorCode } = lastSourceActivity
            const { UserFilename } = JSON.parse(ActivityData)
            handleErrorToast(ErrorCode, UserFilename, 'init')
          }
        } else {
          await getContentsUpdateContentsCallback({ set, snapshot })
          if (ActivityData) {
            const { RemovedByLimits } = JSON.parse(ActivityData)
            if (RemovedByLimits > 0) {
              toastShow(
                getIntl().formatMessage({
                  id: 'Load.ContentsSouces.LimitContentMessageInfo'
                }),
                TOAST_VARIANT_INFO,
                'source--limit-content--error'
              )
            }
          }
        }
      }
    }
  } catch (error) {
    logAppError({
      Component: 'SourceEdit',
      Method: 'getLastSourceActivityCallback',
      appError: error
    })
  }
}

const getSourceActivityCallback = async (
  { set, snapshot },
  callN,
  idA,
  idSourceParams
) => {
  const { idSource, idType } = await snapshot.getPromise(sourceState)
  const { idActivity } = await snapshot.getPromise(modalShowHistoryState)

  const activityId = idActivity || idA

  try {
    const result = await getUpdatedSourceActivity(
      idSourceParams || idSource,
      activityId
    )
    if (result) {
      const { Status, ActivityData } = result
      if (callN >= MAX_CALLS) {
        if (Status === 2) {
          toastShow(
            getIntl().formatMessage({ id: 'Load.ContentsSouces.LoadSucces' }),
            TOAST_VARIANT_SUCCESS,
            'load--success'
          )
          await Promise.all([
            getNumberOfContentsCallback(
              { set, snapshot },
              MAX_CALLS,
              idSource,
              idType
            ),
            getLastUpdateContentsCallback({ set, snapshot }, idSource, idType),
            getContentsUpdateContentsCallback(
              { set, snapshot },
              idSource,
              idType
            )
          ])
          if (ActivityData) {
            const { RemovedByLimits } = JSON.parse(ActivityData)
            if (RemovedByLimits > 0) {
              toastShow(
                getIntl().formatMessage({
                  id: 'Load.ContentsSouces.LimitContentMessageInfo'
                }),
                TOAST_VARIANT_INFO,
                'source--limit-content--error'
              )
            }
          }
        } else if (Status === -1) {
          await getNumberOfContentsCallback(
            { set, snapshot },
            MAX_CALLS,
            idSource,
            idType
          )
          if (ActivityData) {
            const { ErrorCode } = result
            const { UserFilename } = JSON.parse(ActivityData)
            handleErrorToast(ErrorCode, UserFilename, 'save')
          }
        } else if (Status === 0 || Status === 1) {
          let toastMessage
          if (idType === SOURCE_ID_TYPE_FILE) {
            if (ActivityData) {
              const { UserFilename } = JSON.parse(ActivityData)
              toastMessage = (
                <FormattedMessage
                  id='Load.ContentsSouces.LoadAfter10secFile'
                  values={{
                    FILE: UserFilename,
                    strong: (chunks) => <strong>{chunks}</strong>
                  }}
                />
              )
            }
          } else {
            toastMessage = getIntl().formatMessage({
              id: 'Load.ContentsSouces.LoadAfter10secUrl'
            })
          }
          toastShow(
            toastMessage,
            TOAST_VARIANT_WARNING,
            'source--load--warning'
          )
        }
      } else if (
        callN < MAX_CALLS &&
        (Status === 1 || Status === 0 || Status === -1)
      ) {
        set(modalShowHistoryState, (prev) => ({
          ...prev,
          callNum: callN + 1
        }))
      } else {
        set(modalShowHistoryState, (prev) => ({
          ...prev,
          callNum: MAX_CALLS + 1
        }))
      }
    } else {
      set(modalShowHistoryState, (prev) => ({
        ...prev,
        callNum: callN + 1
      }))
    }
  } catch (error) {
    logAppError({
      Component: 'SourceEdit',
      Method: 'getSourceActivityCallback',
      appError: error
    })
  }
}

export const getLastUpdateContentsCallback = async (
  { set, snapshot },
  idS = 0,
  idT = 0
) => {
  const { idSource, idType } = await snapshot.getPromise(sourceState)
  const sourceId = idS || idSource
  const sourceType = idT || idType

  try {
    const [lastUpdateData, updateHistory] = await Promise.all([
      getLastUpdateContents(sourceId),
      getListOfStatus(sourceId, sourceType)
    ])

    let otherActivity

    if (sourceType === SOURCE_ID_TYPE_FILE) {
      otherActivity = (
        await getSourceActivity(sourceId, {
          PageSize: 5,
          PageNumber: 1
        })
      ).items
    }

    set(lastUpdatedState, (prev) => ({
      ...prev,
      ...lastUpdateData,
      loaded: true,
      loading: false,
      error: lastUpdateData.updateDate === '',
      isEnabled: lastUpdateData.updateDate !== ''
    }))
    set(modalShowHistoryState, (prev) => ({
      ...prev,
      updateHistory: updateHistory.items,
      otherActivity,
      loading: false,
      loaded: true
    }))
  } catch (error) {
    logAppError({
      Component: 'SourceEdit',
      Method: 'initLastUpdate',
      appError: error
    })
    set(lastUpdatedState, (prev) => ({
      ...prev,
      error: true,
      loading: false,
      loaded: true
    }))
  }
}

export const getNumberOfContentsCallback = async (
  { set, snapshot },
  callN,
  idS = 0,
  idT = 0
) => {
  const { idSource, idType } = await snapshot.getPromise(sourceState)
  const sourceId = idS || idSource
  const sourceType = idT || idType
  try {
    const contentData = await getContents(sourceId)

    if (contentData.totalItems === 0 && sourceType === SOURCE_ID_TYPE_URL) {
      if (callN >= MAX_CALLS) {
        toastDismiss('source--load--warning')
        toastShow(
          getIntl().formatMessage({
            id: 'Load.ContentsSouces.SourceNoContentWarning'
          }),
          TOAST_VARIANT_WARNING,
          'source-no-content--warning'
        )
        set(sourceState, (prev) => ({ ...prev, urlDisabled: false }))
        set(contentsState, (prev) => ({
          ...prev,
          loading: false,
          loaded: true,
          error: true,
          showBadge: false
        }))
      } else {
        set(contentsState, (prev) => ({
          ...prev,
          callNum: callN + 1,
          loading: true
        }))
      }
    } else {
      set(contentsState, (prev) => ({
        ...prev,
        callNum: MAX_CALLS + 1,
        ...contentData,
        loaded: true,
        loading: false,
        showBadge: false,
        error: false
      }))
    }
  } catch (error) {
    logAppError({
      Component: 'SourceEdit',
      Method: 'getNumberOfContentsCallback',
      appError: error
    })
    set(contentsState, (prev) => ({
      ...prev,
      error: true,
      loading: false,
      loaded: true,
      showBadge: false
    }))
  }
}

export const recallContentsCallback =
  ({ set, snapshot }) =>
  async (callN) => {
    const { idSource, idType } = await snapshot.getPromise(sourceState)
    await getNumberOfContentsCallback(
      { set, snapshot },
      callN,
      idSource,
      idType
    )
  }

export const recallActivityCallback =
  ({ set, snapshot }) =>
  async (callN) => {
    await getSourceActivityCallback({ set, snapshot }, callN)
  }

export const saveCallback =
  ({ set, snapshot }) =>
  async () => {
    dismissAllToasts()
    const data = await snapshot.getPromise(sourceState)
    const updateFrequencyString = await snapshot.getPromise(
      updateFrequencyStringSelector
    )

    const params = {
      ...data,
      updateFrequency: {
        ...data.updateFrequency,
        frequencyString: updateFrequencyString
      }
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    try {
      if (data.idSource === 0) {
        set(pageState, (prev) => ({
          ...prev,
          loading: true,
          loadingMsg: getIntl().formatMessage({
            id: 'Load.ContentsSouces.LoadingText'
          })
        }))

        const idList = storeGet(STORE_KEY_ID_LIST)
        const newIdSource = await insertSource(params)
        params.idSource = newIdSource

        await saveSharedLists(newIdSource, [idList])

        const sourceData = await getSourceById(newIdSource)
        set(sourceState, (prev) => ({
          ...prev,
          idSource: newIdSource,
          ...sourceData,
          isEnabled: true
        }))
      } else {
        set(sourceState, (prev) => ({
          ...prev,
          urlDisabled: true
        }))
        await updateSource(params)
      }

      let idActivity = 0
      if (data.idType === SOURCE_ID_TYPE_URL) {
        idActivity = await updateContents(params.idSource)
      } else if (
        data.idType === SOURCE_ID_TYPE_FILE &&
        params.contentfile.file
      ) {
        idActivity = await uploadFile(
          params.contentfile.file,
          params.idSource,
          params.updateType
        )
      }

      set(contentsState, (prev) => ({
        ...prev,
        showBadge: data.idSource === 0,
        callNum: 0
      }))

      if (idActivity) {
        set(modalShowHistoryState, (prev) => ({
          ...prev,
          callNum: 0,
          idActivity
        }))
      } else {
        if (params.idType === SOURCE_ID_TYPE_URL)
          toastShow(
            getIntl().formatMessage({ id: 'Load.ContentsSouces.LoadSucces' }),
            TOAST_VARIANT_SUCCESS,
            'load--success'
          )
      }
      set(contentsState, (prev) => ({
        ...prev,
        loading: true
      }))
      set(lastUpdatedState, (prev) => ({
        ...prev,
        loading: true,
        error: false,
        isEnabled: false
      }))
      set(modalShowHistoryState, (prev) => ({
        ...prev,
        loading: true
      }))

      await Promise.all([
        getLastUpdateContentsCallback(
          { set, snapshot },
          params.idSource,
          params.idType
        ),
        getSourceActivityCallback(
          { set, snapshot },
          idActivity ? 0 : MAX_CALLS + 1,
          idActivity,
          params.idSource
        )
      ])

      params.idType === SOURCE_ID_TYPE_FILE &&
        !params.contentfile.file &&
        toastShow(
          getIntl().formatMessage({ id: 'Load.Alerts.SuccessUpdate' }),
          TOAST_VARIANT_SUCCESS,
          'upload--success'
        )
      set(pageState, (prev) => ({
        ...prev,
        loadingMsg: '',
        loading: false
      }))
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'SourceEdit',
        Method: 'saveCallback'
      })
    }

    set(sourceState, (prev) => ({
      ...prev,
      isDirty: false,
      updateType: 0,
      contentfile: {
        variant: UPLOADER_VARIANT_DEFAULT,
        file: undefined,
        isDirty: false,
        warning: false
      }
    }))
  }

export const saveSharedListsCallback =
  ({ set, snapshot, reset }) =>
  async () => {
    const { selectAll, sharedLists } = await snapshot.getPromise(
      modalSharedListsState
    )
    const { idSource } = await snapshot.getPromise(sourceState)

    try {
      set(modalSharedListsState, (prev) => ({ ...prev, show: false }))
      const result = await saveSharedLists(
        idSource,
        selectAll ? [0] : sharedLists
      )
      if (result) {
        set(sourceState, (prev) => ({ ...prev, selectAll, sharedLists }))
        toastShow(
          getIntl().formatMessage({ id: 'Load.MailupCommon.OperationSucces' }),
          TOAST_VARIANT_SUCCESS,
          'source-save-shared-lists--success'
        )
      }
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'SourceEdit',
        Method: 'saveSharedListsCallback'
      })
      toastShow(
        getIntl().formatMessage({
          id: 'Load.AutomationWorkflow.ErrorSaved'
        }),
        TOAST_VARIANT_ERROR,
        'source-save-shared-lists--error'
      )
    }
    reset(modalSharedListsState)
  }

export const exportCallback =
  ({ set, snapshot }) =>
  async (type) => {
    const { idSource, name } = await snapshot.getPromise(sourceState)
    set(pageState, (prev) => ({
      ...prev,
      exportJson: true
    }))

    try {
      if (type === MIME_TYPE_CSV) {
        const { Domain } = storeGet(STORE_KEY_ACCOUNT)
        const url = `//${Domain}/example/contents-example.csv`
        const fileContent = await getCsv(url)
        if (fileContent) {
          download(fileContent, `contents-example.csv`, MIME_TYPE_CSV)
        }
      } else if (type === MIME_TYPE_JSON) {
        const fileContent = await getContents(idSource)
        if (fileContent && fileContent.contents) {
          download(
            fileContent.contents,
            `exportSource-${idSource}-${name}.json`,
            MIME_TYPE_JSON,
            true
          )
        }
      }
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'SourceEdit',
        Method: 'exportCallback',
        Message: `Errore durante il download del file ${type}`
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }
    set(pageState, (prev) => ({
      ...prev,
      exportJson: false
    }))
  }

const handleErrorToast = (ErrorCode, UserFilename, status) => {
  let toastMessage
  switch (ErrorCode) {
    case ERROR_CODE_INVALID_EXT:
      toastMessage = (
        <FormattedMessage
          id='Load.ContentsSouces.ErrorInvalidExtension'
          values={{
            FILE: UserFilename,
            strong: (chunks) => <strong>{chunks}</strong>
          }}
        />
      )
      break
    case ERROR_CODE_INVALID_ZIP:
      toastMessage = (
        <FormattedMessage
          id='oad.ContentsSouces.ErrorInvalidZipFormat'
          values={{
            FILE: UserFilename,
            strong: (chunks) => <strong>{chunks}</strong>
          }}
        />
      )
      break
    case ERROR_CODE_PARSING:
    case ERROR_CODE_MAPPING:
      toastMessage = (
        <FormattedMessage
          id='Load.ContentsSouces.ErrorParsing'
          values={{
            FILE: UserFilename,
            strong: (chunks) => <strong>{chunks}</strong>
          }}
        />
      )

      break
    case ERROR_CODE_VALIDATING_HEADER:
      toastMessage = (
        <FormattedMessage
          id='Load.ContentsSouces.ErrorValidatingHeader'
          values={{
            FILE: UserFilename,
            strong: (chunks) => <strong>{chunks}</strong>
          }}
        />
      )

      break
    case ERROR_CODE_EMPTY_FILE:
      toastMessage = (
        <FormattedMessage
          id='Load.ContentsSouces.ErrorEmptyFile'
          values={{
            FILE: UserFilename,
            strong: (chunks) => <strong>{chunks}</strong>
          }}
        />
      )

      break
    case ERROR_CODE_TOO_MANY_CONTENT:
      toastMessage = (
        <FormattedMessage
          id='Load.ContentsSouces.ErrorToManyCOntent'
          values={{
            FILE: UserFilename,
            strong: (chunks) => <strong>{chunks}</strong>
          }}
        />
      )
      getIntl().formatMessage(
        { id: 'Load.ContentsSouces.ErrorToManyCOntent' },
        {
          values: {
            FILE: UserFilename,
            strong: (chunks) => <strong>{chunks}</strong>
          }
        }
      )
      break

    default:
      toastMessage = (
        <FormattedMessage
          id='Load.ContentsSouces.UploadError'
          values={{
            FILE: UserFilename,
            strong: (chunks) => <strong>{chunks}</strong>
          }}
        />
      )
      break
  }
  toastShow(toastMessage, TOAST_VARIANT_ERROR, 'source--error ' + status)
}

export const cleanupCallback =
  ({ reset }) =>
  async () => {
    reset(pageState)
    reset(sourceState)
    reset(contentsState)
    reset(listState)
    reset(modalShowHistoryState)
    reset(lastUpdatedState)
    reset(modalSharedListsState)
  }

const dismissAllToasts = () => {
  toastDismiss('source--error init')
  toastDismiss('source--error save')
  toastDismiss('source-save-shared-lists--error')
  toastDismiss('source-save-shared-lists--success')
  toastDismiss('upload--success')
  toastDismiss('source-no-content--warning')
  toastDismiss('source--load--warning')
  toastDismiss('load--success')
  toastDismiss('source--limit-content--error')
}
